import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useRegion } from '../hooks/useRegion';
import { hireDaysToTarget } from '../lib/equipmentInsights';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { TrendingUp, Target, Calendar, Loader2, Boxes, Plus } from 'lucide-react';

// Custom tooltip component - defined outside to prevent recreation
function ChartTooltip({ active, payload, label, formatCurrency }) {
  if (active && payload && payload.length > 0) {
    const dataPoint = payload[0]?.payload;
    if (!dataPoint) return null;
    
    // Format the date from the raw date string
    const dateLabel = dataPoint.date 
      ? new Date(dataPoint.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : label;
    
    const historicalValue = dataPoint.historical;
    const projectedValue = dataPoint.projected;
    const targetValue = dataPoint.target;
    
    return (
      <div className="bg-parchment text-popover-foreground rounded-cards shadow-modal p-3 text-sm">
        <p className="mb-1">{dateLabel}</p>
        {historicalValue !== null && historicalValue !== undefined && (
          <p className="text-foreground">Actual: {formatCurrency(historicalValue)}</p>
        )}
        {projectedValue !== null && projectedValue !== undefined && historicalValue === null && (
          <p className="text-clay">Projected: {formatCurrency(projectedValue)}</p>
        )}
        {targetValue !== null && targetValue !== undefined && (
          <p className="text-muted-foreground text-xs mt-1">Target: {formatCurrency(targetValue)}</p>
        )}
      </div>
    );
  }
  return null;
}

export function ROIProjectionCard({ selectedKitId: externalSelectedKitId, onKitSelect }) {
  const navigate = useNavigate();
  const { 
    roiProjection, roiProjectionLoading, fetchROIProjection,
    equipmentKits
  } = useAppStore();
  const { formatCurrency } = useRegion();
  
  const [internalSelectedKitId, setInternalSelectedKitId] = useState(null);
  const [targetROI, setTargetROI] = useState(100);
  const [rateAdjustment, setRateAdjustment] = useState(0);
  
  // Use external kit ID if provided, otherwise use internal state
  const selectedKitId = externalSelectedKitId !== undefined ? externalSelectedKitId : internalSelectedKitId;
  const setSelectedKitId = (id) => {
    setInternalSelectedKitId(id);
    if (onKitSelect) onKitSelect(id);
  };
  
  useEffect(() => {
    fetchROIProjection();
    // Note: equipmentKits are already fetched by EquipmentPage parent
  }, [fetchROIProjection]);

  // Get the effective selected kit ID (auto-select first kit if none selected)
  const effectiveKitId = useMemo(() => {
    if (selectedKitId) return selectedKitId;
    if (equipmentKits.length > 0) return equipmentKits[0].id;
    return null;
  }, [selectedKitId, equipmentKits]);

  // Get the selected kit
  const selectedKit = useMemo(() => {
    return equipmentKits.find(k => k.id === effectiveKitId) || null;
  }, [equipmentKits, effectiveKitId]);

  // Get the effective day rate (from kit or fallback to portfolio average)
  const effectiveDayRate = useMemo(() => {
    if (selectedKit && selectedKit.total_daily_rate > 0) {
      return selectedKit.total_daily_rate;
    }
    return roiProjection?.rates?.average_day_rate || 0;
  }, [selectedKit, roiProjection]);

  // Get the kit value (purchase price) for target calculations
  const kitValue = useMemo(() => {
    if (selectedKit && selectedKit.total_purchase_price > 0) {
      return selectedKit.total_purchase_price;
    }
    return roiProjection?.portfolio?.total_value || 0;
  }, [selectedKit, roiProjection]);

  // Get the kit's total income (income generated by equipment IN this kit)
  const kitIncome = useMemo(() => {
    if (selectedKit) {
      return selectedKit.total_income || 0;
    }
    return roiProjection?.portfolio?.total_income || 0;
  }, [selectedKit, roiProjection]);

  // Get the kit's total days worked
  const kitDays = useMemo(() => {
    if (selectedKit) {
      return selectedKit.total_days || 0;
    }
    return roiProjection?.portfolio?.total_days_worked || 0;
  }, [selectedKit, roiProjection]);
  
  // Fixed utilization rate for calculations (2 days per week default)
  const effectiveUtilizationRate = useMemo(() => {
    if (roiProjection?.rates?.utilization_days_per_week) {
      return Math.max(0.5, roiProjection.rates.utilization_days_per_week);
    }
    return 2;
  }, [roiProjection]);

  // Memoized tooltip renderer
  const renderTooltip = useCallback((props) => (
    <ChartTooltip {...props} formatCurrency={formatCurrency} />
  ), [formatCurrency]);

  // Calculate chart data with projections based on selected kit
  const chartData = useMemo(() => {
    if (!roiProjection?.has_data || !selectedKit) return [];
    
    const { portfolio, historical_data } = roiProjection;
    const adjustedRate = effectiveDayRate * (1 + rateAdjustment / 100);
    // Target amount = percentage of KIT value to earn back
    // 100% = break-even (earn back what the kit cost)
    // 200% = double your money on the kit
    const targetAmount = kitValue * (targetROI / 100);
    
    // Collect all dates first to determine time span
    const historicalDates = (historical_data || []).map(d => new Date(d.date));
    const today = new Date();
    
    // Calculate expected end date based on projections
    const expectedEndDate = new Date(today);
    expectedEndDate.setMonth(expectedEndDate.getMonth() + 24); // Max 24 months projection
    
    // Get min and max years in our data range
    const minYear = historicalDates.length > 0 
      ? Math.min(...historicalDates.map(d => d.getFullYear())) 
      : today.getFullYear();
    const maxYear = Math.max(today.getFullYear() + 2, expectedEndDate.getFullYear());
    const yearSpan = maxYear - minYear;
    
    // Format date labels for X-axis based on time span
    const formatDateLabel = (dateStr) => {
      const date = new Date(dateStr);
      const month = date.getMonth();
      const year = date.getFullYear();
      
      if (yearSpan >= 3) {
        if (month === 0) {
          return year.toString();
        }
        return '';
      } else if (yearSpan >= 1) {
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      }
    };
    
    // Format full date for tooltip
    const formatTooltipDate = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };
    
    // Kit-specific historical data: Use actual usage_history from equipment in the kit
    const data = [];
    const currentIncome = kitIncome;
    const kitUsageHistory = selectedKit.usage_history || [];
    
    // Track data points for slope calculation
    const historicalPoints = []; // { monthIndex, cumulativeIncome }
    
    // If kit has usage history, plot each historical data point
    if (kitUsageHistory.length > 0) {
      // Group income by month
      const monthlyData = {};
      
      // Sort by date first
      const sortedHistory = [...kitUsageHistory].sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );
      
      // Group by month
      sortedHistory.forEach(entry => {
        const date = new Date(entry.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { income: 0, date: entry.date };
        }
        monthlyData[monthKey].income += entry.income || 0;
      });
      
      // Convert to data points
      const sortedMonths = Object.keys(monthlyData).sort();
      
      // Add starting point (before first income)
      if (sortedMonths.length > 0) {
        const firstDate = new Date(monthlyData[sortedMonths[0]].date);
        firstDate.setMonth(firstDate.getMonth() - 1);
        const dateStr = firstDate.toISOString().split('T')[0];
        data.push({
          date: dateStr,
          displayDate: formatDateLabel(dateStr),
          tooltipDate: formatTooltipDate(dateStr),
          historical: 0,
          projected: null,
          target: targetAmount
        });
        historicalPoints.push({ monthIndex: 0, income: 0 });
      }
      
      // Add each month's cumulative income
      let cumulativeIncome = 0;
      sortedMonths.forEach((monthKey, idx) => {
        cumulativeIncome += monthlyData[monthKey].income;
        const [year, month] = monthKey.split('-');
        const dateStr = `${year}-${month}-01`;
        
        data.push({
          date: dateStr,
          displayDate: formatDateLabel(dateStr),
          tooltipDate: formatTooltipDate(dateStr),
          historical: Math.round(cumulativeIncome),
          projected: null,
          target: targetAmount
        });
        historicalPoints.push({ monthIndex: idx + 1, income: cumulativeIncome });
      });
      
      // Add current point if it's after the last historical point
      const lastDataPoint = data[data.length - 1];
      const lastDate = lastDataPoint ? new Date(lastDataPoint.date) : null;
      
      if (!lastDate || today > lastDate) {
        const dateStr = today.toISOString().split('T')[0];
        data.push({
          date: dateStr,
          displayDate: formatDateLabel(dateStr),
          tooltipDate: formatTooltipDate(dateStr),
          historical: currentIncome,
          projected: currentIncome,
          target: targetAmount,
          isCurrent: true
        });
        historicalPoints.push({ monthIndex: historicalPoints.length, income: currentIncome });
      } else {
        // Update last point to be projection start
        lastDataPoint.historical = currentIncome;
        lastDataPoint.projected = currentIncome;
        lastDataPoint.isCurrent = true;
      }
    } else {
      // Kit has no usage history - show a short lookback period
      const startDate = new Date(today);
      startDate.setMonth(startDate.getMonth() - 1);
      const startDateStr = startDate.toISOString().split('T')[0];
      
      data.push({
        date: startDateStr,
        displayDate: formatDateLabel(startDateStr),
        tooltipDate: formatTooltipDate(startDateStr),
        historical: 0,
        projected: null,
        target: targetAmount
      });
      historicalPoints.push({ monthIndex: 0, income: 0 });
      
      // Add current point
      const todayStr = today.toISOString().split('T')[0];
      data.push({
        date: todayStr,
        displayDate: formatDateLabel(todayStr),
        tooltipDate: formatTooltipDate(todayStr),
        historical: currentIncome,
        projected: currentIncome,
        target: targetAmount,
        isCurrent: true
      });
      historicalPoints.push({ monthIndex: 1, income: currentIncome });
    }
    
    // Calculate the slope (growth rate) from historical data using linear regression
    // This finds the best-fit line through the historical points
    const calculateSlope = (points) => {
      if (points.length < 2) return 0;
      
      const n = points.length;
      let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
      
      points.forEach(p => {
        sumX += p.monthIndex;
        sumY += p.income;
        sumXY += p.monthIndex * p.income;
        sumX2 += p.monthIndex * p.monthIndex;
      });
      
      // Slope formula: (n*sumXY - sumX*sumY) / (n*sumX2 - sumX*sumX)
      const denominator = n * sumX2 - sumX * sumX;
      if (denominator === 0) return 0;
      
      return (n * sumXY - sumX * sumY) / denominator;
    };
    
    // Get the monthly growth rate from historical trajectory
    const historicalSlope = calculateSlope(historicalPoints);
    
    // Calculate expected monthly income based on adjusted rate
    const weeklyIncome = adjustedRate * effectiveUtilizationRate;
    const expectedMonthlyIncome = weeklyIncome * 4.33;
    
    // If we have historical data, blend historical slope with rate adjustment
    // Rate adjustment acts as a multiplier on the projection
    let monthlyGrowthRate;
    if (historicalSlope > 0) {
      // Apply rate adjustment as a modifier to historical growth
      const rateMultiplier = 1 + (rateAdjustment / 100);
      monthlyGrowthRate = historicalSlope * rateMultiplier;
    } else {
      // No history - use expected rate directly
      monthlyGrowthRate = expectedMonthlyIncome;
    }
    
    // Project future income following the same trajectory
    let projectedCumulative = currentIncome;
    const startMonthIndex = historicalPoints.length;
    
    // Project up to 24 months or until we hit 150% of target
    const maxProjection = targetAmount * 1.5;
    
    for (let i = 1; i <= 24; i++) {
      const projectionDate = new Date(today);
      projectionDate.setMonth(projectionDate.getMonth() + i);
      const dateStr = projectionDate.toISOString().split('T')[0];
      
      // Continue at the same slope
      projectedCumulative += monthlyGrowthRate;
      
      data.push({
        date: dateStr,
        displayDate: formatDateLabel(dateStr),
        tooltipDate: formatTooltipDate(dateStr),
        historical: null,
        projected: Math.round(projectedCumulative),
        target: targetAmount
      });
      
      // Stop if we've exceeded the view window
      if (projectedCumulative >= maxProjection) break;
    }
    
    return data;
  }, [roiProjection, selectedKit, effectiveDayRate, kitValue, kitIncome, effectiveUtilizationRate, targetROI, rateAdjustment]);
  
  // Calculate insights based on chart data (matches the visual projection)
  const insights = useMemo(() => {
    if (!roiProjection?.has_data || !selectedKit || chartData.length === 0) return null;
    
    const targetAmount = kitValue * (targetROI / 100);
    
    // Check if target achieved: kit income >= target income
    const isComplete = kitIncome >= targetAmount;
    
    // Calculate kit ROI
    const kitROI = kitValue > 0 ? ((kitIncome - kitValue) / kitValue * 100) : 0;
    
    if (isComplete) {
      return {
        targetAmount,
        kitROI,
        isComplete,
        targetDate: null,
        hireDaysRemaining: 0
      };
    }
    
    // Find where the projected line crosses the target by looking at chart data
    let targetDate = null;
    let monthsToTarget = 0;
    
    for (let i = 0; i < chartData.length; i++) {
      const point = chartData[i];
      const value = point.projected ?? point.historical ?? 0;
      
      if (value >= targetAmount) {
        targetDate = new Date(point.date);
        // Count months from now to this point
        const now = new Date();
        monthsToTarget = (targetDate.getFullYear() - now.getFullYear()) * 12 + (targetDate.getMonth() - now.getMonth());
        break;
      }
    }
    
    // If we didn't find a crossing point in the data, estimate based on the last projected value
    if (!targetDate && chartData.length > 1) {
      const lastPoint = chartData[chartData.length - 1];
      const lastValue = lastPoint.projected ?? lastPoint.historical ?? 0;
      
      // If still below target at end of projection, extrapolate
      if (lastValue < targetAmount && lastValue > kitIncome) {
        // Calculate monthly growth rate from projection
        const firstProjectedIdx = chartData.findIndex(p => p.projected !== null && p.historical === null);
        if (firstProjectedIdx > 0 && firstProjectedIdx < chartData.length - 1) {
          const monthlyGrowth = (lastValue - kitIncome) / (chartData.length - firstProjectedIdx);
          if (monthlyGrowth > 0) {
            const additionalMonthsNeeded = Math.ceil((targetAmount - lastValue) / monthlyGrowth);
            targetDate = new Date(lastPoint.date);
            targetDate.setMonth(targetDate.getMonth() + additionalMonthsNeeded);
            monthsToTarget = (targetDate.getFullYear() - new Date().getFullYear()) * 12 + (targetDate.getMonth() - new Date().getMonth());
          }
        }
      }
    }
    
    const hireDaysRemaining = hireDaysToTarget({
      cost: kitValue, income: kitIncome, dayRate: effectiveDayRate, targetPercent: targetROI, rateAdjustment,
    });
    
    return {
      targetAmount,
      kitROI,
      isComplete,
      targetDate,
      hireDaysRemaining
    };
  }, [roiProjection, selectedKit, chartData, kitValue, kitIncome, targetROI, effectiveDayRate, rateAdjustment]);

  // Loading state - only check roiProjection loading
  // (kitsLoading is handled by parent EquipmentPage)
  if (roiProjectionLoading) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  // No kits - prompt user to create one
  if (equipmentKits.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-pill bg-mist flex items-center justify-center">
              <TrendingUp className="w-[18px] h-[18px] text-foreground" />
            </div>
            <CardTitle>Kit-based ROI projection</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="p-4 bg-mist rounded-pill mb-4">
              <Boxes className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="section-title mb-2">Create a kit to see projections</h3>
            <p className="text-muted-foreground text-sm max-w-md mb-6">
              ROI projections work best when based on the equipment you actually bring to jobs. 
              Create a kit with your typical gear loadout to see realistic income projections.
            </p>
            <Button onClick={() => navigate('/equipment')} className="gap-2">
              <Plus className="w-4 h-4" />
              Create your first kit
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No equipment data
  if (!roiProjection?.has_data) {
    return null;
  }

  const { rates } = roiProjection;
  
  // Calculate kit ROI percentage
  const kitROI = kitValue > 0 
    ? ((kitIncome - kitValue) / kitValue * 100)
    : 0;

  // Adjusted rate for display
  const adjustedRate = effectiveDayRate * (1 + rateAdjustment / 100);

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-pill bg-mist flex items-center justify-center">
              <TrendingUp className="w-[18px] h-[18px] text-foreground" />
            </div>
            <CardTitle>Kit-based ROI projection</CardTitle>
          </div>
          {/* Kit Selector - Top Right */}
          <Select value={effectiveKitId || ''} onValueChange={setSelectedKitId}>
            <SelectTrigger className="w-full sm:w-[240px]">
              <SelectValue placeholder="Choose a kit" />
            </SelectTrigger>
            <SelectContent>
              {equipmentKits.map((kit) => (
                <SelectItem key={kit.id} value={kit.id}>
                  <span>{kit.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      {/* Stats Bar - Always visible, responsive grid */}
      <CardContent className="pt-2 pb-4">
        {/* Mobile: 2 columns, Desktop: flex */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {/* Kit Value */}
          <div className="text-center py-2">
            <p className="text-2xl sm:text-3xl font-light tracking-tight truncate">{formatCurrency(kitValue)}</p>
            <p className="text-sm text-muted-foreground">Kit value</p>
          </div>
          
          {/* Kit Income */}
          <div className="text-center py-2">
            <p className="text-2xl sm:text-3xl font-light tracking-tight text-moss truncate">{formatCurrency(kitIncome)}</p>
            <p className="text-sm text-muted-foreground">Kit income</p>
          </div>
          
          {/* Kit ROI */}
          <div className="text-center py-2">
            <p className={`text-2xl sm:text-3xl font-light tracking-tight ${kitROI >= 0 ? 'text-moss' : 'text-brick'}`}>
              {kitROI >= 0 ? '+' : ''}{kitROI.toFixed(1)}%
            </p>
            <p className="text-sm text-muted-foreground">ROI</p>
          </div>
          
          {/* Kit Day Rate - shows adjusted rate in real-time */}
          <div className="text-center py-2">
            <p className="text-2xl sm:text-3xl font-light tracking-tight truncate">{formatCurrency(adjustedRate)}</p>
            <p className="text-sm text-muted-foreground">Per day value</p>
          </div>
        </div>
      </CardContent>
      
      <CardContent className="pt-0 border-t border-line">
        <div className="pt-4 space-y-4">
          {/* Inline Controls */}
          <div className="flex flex-wrap items-center gap-4 lg:gap-6">
            {/* Target */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">Target:</Label>
              <div className="w-20">
                <Slider
                  value={[targetROI]}
                  onValueChange={([value]) => setTargetROI(value)}
                  min={50}
                  max={300}
                  step={10}
                />
              </div>
              <Badge variant="outline" className="text-xs">{targetROI}%</Badge>
            </div>

            {/* Rate Adjustment */}
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">Kit rate adj.:</Label>
              <div className="w-20">
                <Slider
                  value={[rateAdjustment]}
                  onValueChange={([value]) => setRateAdjustment(value)}
                  min={-20}
                  max={50}
                  step={5}
                />
              </div>
              <Badge 
                variant={rateAdjustment > 0 ? 'default' : rateAdjustment < 0 ? 'destructive' : 'outline'}
                className="text-xs"
              >
                {rateAdjustment > 0 ? '+' : ''}{rateAdjustment}%
              </Badge>
            </div>
          </div>

          {/* Chart */}
          <div className="h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="hsl(var(--line))" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 13, fill: 'hsl(var(--ink-muted))' }}
                      axisLine={{ stroke: 'hsl(var(--line))' }}
                      tickLine={{ stroke: 'hsl(var(--line))' }}
                      interval="preserveStartEnd"
                      tickFormatter={(value) => {
                        if (!value) return '';
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 13, fill: 'hsl(var(--ink-muted))' }}
                      axisLine={{ stroke: 'hsl(var(--line))' }}
                      tickLine={{ stroke: 'hsl(var(--line))' }}
                      tickFormatter={(value) => {
                        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                        return value;
                      }}
                    />
                    <Tooltip 
                      content={renderTooltip}
                      cursor={{ stroke: 'hsl(var(--line-strong))', strokeWidth: 1, strokeDasharray: '3 3' }}
                    />
                    
                    {/* Target line - a moss rule (100% is break-even) */}
                    {insights?.targetAmount && (
                      <ReferenceLine 
                        y={insights.targetAmount} 
                        stroke="hsl(var(--moss))" 
                        strokeWidth={1.5}
                        label={{ 
                          value: `${targetROI}%`, 
                          position: 'insideTopLeft',
                          fill: 'hsl(var(--moss))',
                          fontSize: 13
                        }}
                      />
                    )}
                    
                    {/* Historical area - ink series, ink 8% fill */}
                    <Area
                      type="monotone"
                      dataKey="historical"
                      stroke="hsl(var(--ink))"
                      strokeWidth={2}
                      fill="hsl(var(--ink) / 0.08)"
                      connectNulls={false}
                      activeDot={{ r: 4, fill: 'hsl(var(--ink))' }}
                    />
                    
                    {/* Projected area - clay comparison line, dashed */}
                    <Area
                      type="monotone"
                      dataKey="projected"
                      stroke="hsl(var(--clay))"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      fill="hsl(var(--ink) / 0.08)"
                      connectNulls={false}
                      activeDot={{ r: 4, fill: 'hsl(var(--clay))' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              
              {/* Bottom row: Legend + Insights */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                {/* Legend */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-0.5 bg-ink"></div>
                    <span className="text-muted-foreground text-sm">Actual</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 border-t-2 border-dashed border-clay"></div>
                    <span className="text-muted-foreground text-sm">Projected</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-0.5 bg-moss"></div>
                    <span className="text-muted-foreground text-sm">Target</span>
                  </div>
                </div>

                {/* Compact Insights */}
                {insights && !insights.isComplete && insights.targetDate && (
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Target: <strong className="text-foreground">{insights.targetDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Target className="w-3.5 h-3.5" />
                      <span><strong className="text-foreground">{insights.hireDaysRemaining}</strong> hire days remaining</span>
                    </div>
                  </div>
                )}
                
                {insights?.isComplete && (
                  <div className="flex items-center gap-2 text-moss text-sm">
                    <TrendingUp className="w-4 h-4" />
                    <span>Target reached! {insights.kitROI.toFixed(1)}% ROI</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
  );
}
